import type { ReactNode } from 'react';
import { Numeric } from './Numeric.js';
import styles from './AmountWithAux.module.css';

export type AmountWithAuxProps = {
  value: unknown;
  /** Default MAD; pass "" to omit when using `auxiliary` only (e.g. %). */
  currency?: string;
  /** Shown muted after amount (e.g. HT / TTC per docs/PRICING.md). */
  pricingMode?: 'HT' | 'TTC';
  /**
   * Full auxiliary tail (muted). Use for `%` or when `currency`/`pricingMode` don’t apply.
   * If set, overrides the built `currency · pricingMode` string.
   */
  auxiliary?: string;
  empty?: ReactNode;
  decimals?: number;
};

/** Tabular amount + muted auxiliary (currency, HT/TTC, %, …). Primary figure in mono; tail in body muted. */
export function AmountWithAux({
  value,
  currency = 'MAD',
  pricingMode,
  auxiliary,
  empty = '—',
  decimals = 2,
}: AmountWithAuxProps) {
  if (value == null || value === '') {
    return <span className={styles.empty}>{empty}</span>;
  }
  const n = typeof value === 'number' ? value : Number(value);
  const main = Number.isFinite(n) ? n.toFixed(decimals) : String(value);
  const aux =
    auxiliary !== undefined
      ? auxiliary.startsWith(' ')
        ? auxiliary
        : ` ${auxiliary}`
      : currency?.trim()
        ? ` ${currency.trim()}${pricingMode ? ` · ${pricingMode}` : ''}`
        : pricingMode
          ? ` ${pricingMode}`
          : undefined;

  return (
    <span className={styles.wrap}>
      <Numeric>{main}</Numeric>
      {aux ? <span className={styles.aux}>{aux}</span> : null}
    </span>
  );
}
